import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent, RowSelectedEvent, IsFullWidthRowParams } from 'ag-grid-community';
import { notification, Checkbox } from 'antd';
import { getCashierTransactionCols } from 'constants/ag-grid-table';
import { useGetGuestOrderDetail } from 'hooks/useGetGuestTransactions';
import { formatCurrency } from 'utils';
import { useMutation } from '@tanstack/react-query';
import { editTransactionNote } from 'services/api/module/booking.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

function TransactionTable({
  orderId,
  setTransactionIds,
  setCancelledTransactionIds,
  isInvoiced,
  hideDeleted,
  onCancelledCountChange,
  setIsSplitTransactions,
  t
}: {
  orderId: number;
  setTransactionIds: (selectedKey: any) => void;
  isInvoiced: boolean;
  hideDeleted: boolean;
  onCancelledCountChange: (cancelled: number) => void;
  setCancelledTransactionIds: (selectedKey: any) => void;
  t: (key: string) => string;
  setIsSplitTransactions: (isSplitTransactions: boolean) => void;
}) {
  const { data: orderDetail } = useGetGuestOrderDetail(orderId);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const gridRef = useRef<any>(null);

  const { mutateAsync: mutateEditTransactionNode } = useMutation({
    mutationFn: (params: any) => editTransactionNote(params)
  });

  const handleChangeSelectedRow = (event: RowSelectedEvent) => {
    // Skip if this is a group header row
    if (event.node.data?.isGroupHeader) {
      return;
    }

    // If this is a POS order
    if (orderDetail?.order_type === 'pos' && event.node.data?.reference) {
      const selectedReference = event.node.data.reference;

      if (event.node.isSelected()) {
        // SELECTING: Check if there are already selected transactions with different references
        const currentlySelectedNodes = event.api.getSelectedNodes();
        const existingReferences = currentlySelectedNodes
          .filter(node => node.id !== event.node.id && node.data?.reference) // Exclude current node
          .map(node => node.data.reference);

        // If there are existing selections with different references, show error and deselect
        if (existingReferences.length > 0 && !existingReferences.includes(selectedReference)) {
          notification.error({
            message: t('common.message.posTransaction')
          });

          // Deselect the newly selected transaction
          event.node.setSelected(false);
          return;
        }

        // Find all transactions with the same reference
        const nodesWithSameReference: any[] = [];
        event.api.forEachNode(node => {
          if (node.data?.reference === selectedReference) {
            nodesWithSameReference.push(node);
          }
        });

        // Select all transactions with the same reference
        nodesWithSameReference.forEach(node => {
          node.setSelected(true);
        });
      } else {
        // DESELECTING: Unselect all transactions with the same reference
        const nodesWithSameReference: any[] = [];
        event.api.forEachNode(node => {
          if (node.data?.reference === selectedReference) {
            nodesWithSameReference.push(node);
          }
        });

        // Deselect all transactions with the same reference
        nodesWithSameReference.forEach(node => {
          node.setSelected(false);
        });
      }
    }

    const transactionIds = event.api
      .getSelectedNodes()
      .filter(item => !item.data?.isGroupHeader && !item.data.is_deleted)
      .map(item => item.data.id);
    setTransactionIds(transactionIds);

    const splitTransactions = event.api
      .getSelectedNodes()
      .filter(item => !item.data?.isGroupHeader && item.data.is_split)
      .map(item => item.data.id);
    setIsSplitTransactions(splitTransactions.length > 0);

    const cancelledTransactionIds = event.api
      .getSelectedNodes()
      .filter(item => !item.data?.isGroupHeader && item.data.is_deleted)
      .map(item => item.data.id);
    setCancelledTransactionIds(cancelledTransactionIds);

    // Refresh full width rows to update group checkboxes
    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells({ force: true });
    }
  };

  const handleChangeCellValue = async (event: CellValueChangedEvent) => {
    if (event.colDef.field === 'note') {
      const updatedNotice = event.newValue;
      const transactionId = event.data.id;
      try {
        await mutateEditTransactionNode({ transaction_id: transactionId, note: updatedNotice });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_GUEST]
        });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
        });
        notification.success({
          message: t('common.message.noteEdit')
        });
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      }
    }
  };

  const cashierTransactionCols = useMemo(() => {
    return getCashierTransactionCols(t);
  }, [t]);

  useEffect(() => {
    if (orderDetail?.transactions) {
      const cancelledCount = orderDetail.transactions.filter(item => item.is_deleted).length;
      onCancelledCountChange(cancelledCount);
    }
  }, [orderDetail, onCancelledCountChange]);

  const filteredTransactions = useMemo(() => {
    if (!orderDetail?.transactions) return [];
    return hideDeleted
      ? orderDetail.transactions.filter(t => !t.is_deleted)
      : orderDetail.transactions;
  }, [orderDetail?.transactions, hideDeleted]);

  // Group transactions by reference (only if they have a reference)
  const groupedTransactions = useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    const noReference: any[] = [];

    filteredTransactions.forEach(transaction => {
      if (transaction.reference && transaction.reference.trim() !== '') {
        const reference = transaction.reference;
        if (!groups[reference]) {
          groups[reference] = [];
        }
        groups[reference].push(transaction);
      } else {
        noReference.push(transaction);
      }
    });

    return { groups, noReference };
  }, [filteredTransactions]);

  const rowData = useMemo(() => {
    const rows: any[] = [];
    const groupedRows: any[] = [];

    Object.entries(groupedTransactions.groups).forEach(([reference, transactions]) => {
      if (transactions.length >= 2) {
        const totalAmount = transactions.reduce((sum, t) => sum + (t.amount * t.qty || 0), 0);

        groupedRows.push({
          isGroupHeader: true,
          reference,
          totalAmount,
          transactionCount: transactions.length,
          transactions
        });

        if (expandedGroups.has(reference)) {
          groupedRows.push(...transactions);
        }
      } else {
        rows.push(...transactions);
      }
    });

    rows.push(...groupedTransactions.noReference);

    rows.push(...groupedRows);

    return rows;
  }, [groupedTransactions, expandedGroups]);

  const toggleGroup = (reference: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reference)) {
        newSet.delete(reference);
      } else {
        newSet.add(reference);
      }
      return newSet;
    });
  };

  const isFullWidthRow = (params: IsFullWidthRowParams) => {
    return params.rowNode.data?.isGroupHeader === true;
  };

  const getGroupSelectionState = useCallback(
    (transactions: any[]) => {
      if (!gridRef.current?.api) return { checked: false, indeterminate: false };

      const transactionIds = transactions.map(t => t.id);
      let selectedCount = 0;
      let totalCount = 0;

      gridRef.current.api.forEachNode((node: any) => {
        if (node.data && !node.data.isGroupHeader && transactionIds.includes(node.data.id)) {
          totalCount++;
          if (node.isSelected()) {
            selectedCount++;
          }
        }
      });

      const allSelected = selectedCount === totalCount && totalCount > 0;
      const someSelected = selectedCount > 0 && selectedCount < totalCount;

      return {
        checked: allSelected,
        indeterminate: someSelected
      };
    },
    [gridRef]
  );

  const handleGroupSelection = useCallback(
    (transactions: any[], checked: boolean, event?: any) => {
      if (event) {
        event.stopPropagation();
      }

      if (!gridRef.current?.api) return;

      const transactionIds = transactions.map(t => t.id);

      gridRef.current.api.forEachNode((node: any) => {
        if (node.data && !node.data.isGroupHeader && transactionIds.includes(node.data.id)) {
          node.setSelected(checked);
        }
      });
    },
    [gridRef]
  );

  const fullWidthCellRenderer = (params: any) => {
    const { reference, totalAmount, transactionCount, transactions } = params.data;
    const isExpanded = expandedGroups.has(reference);
    const selectionState = getGroupSelectionState(transactions);

    return (
      <div
        style={{
          padding: '12px 16px',
          backgroundColor: '#f5f5f5',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #d9d9d9'
        }}
      >
        {!isInvoiced && (
          <Checkbox
            checked={selectionState.checked}
            indeterminate={selectionState.indeterminate}
            onChange={e => handleGroupSelection(transactions, e.target.checked, e)}
            onClick={e => e.stopPropagation()}
            style={{ marginRight: 8 }}
          />
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            cursor: 'pointer'
          }}
          onClick={() => toggleGroup(reference)}
        >
          {isExpanded ? (
            <DownOutlined style={{ marginRight: 8 }} />
          ) : (
            <RightOutlined style={{ marginRight: 8 }} />
          )}
          <span style={{ flex: 1 }}>
            {reference} ({transactionCount} {t('common.items')})
          </span>
          <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
            {t('common.total')}: {formatCurrency(totalAmount)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="pmn-cashier__transaction-table ag-theme-quartz" style={{ width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={cashierTransactionCols}
          selection={{
            mode: 'multiRow',
            checkboxes: !isInvoiced,
            headerCheckbox: !isInvoiced
          }}
          onCellValueChanged={handleChangeCellValue}
          onRowSelected={handleChangeSelectedRow}
          stopEditingWhenCellsLoseFocus
          isFullWidthRow={isFullWidthRow}
          fullWidthCellRenderer={fullWidthCellRenderer}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <h5 className="m-0" style={{ fontWeight: 600 }}>
          {t('cashierPage.balance')}:{' '}
          <span className="text-error">{formatCurrency(orderDetail?.balance || 0)}</span>
        </h5>
      </div>
    </div>
  );
}

export default TransactionTable;
